/* WorkTrigFutureViewRenderer.js */

"use strict";

const EntityGrid = require("@gluenetworks/entity_grid_gl").Grid;

class WorkTrigFutureViewRenderer {
    constructor(model) {
        // this is work trigger grid model
        this._model = model;
        this._gameEditor = this._model.gameEditor;
        this._rootElem = document.createElement("worktrig-future-grid-view-gl");
        this._logger = this._model.logger;
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._model.pagingFeed.on("RefreshView", (this._onFeedRefreshView = this._onFeedRefreshView.bind(this)));
        this._registerGridListeners();
    }

    _registerGridListeners() {
        this._futureGrid.on("UserSorted", (this._onUserSorted = this._onUserSorted.bind(this)));
        this._futureGrid.on("UserSelectedRows", (this._onUserSelectedRows = this._onUserSelectedRows.bind(this)));
    }

    _unregisterListeners() {
        this._model.pagingFeed.removeListener("RefreshView", this._onFeedRefreshView);
        this._unregisterGridListeners();
    }

    _unregisterGridListeners() {
        this._futureGrid.removeListener("UserSorted", this._onUserSorted);
        this._futureGrid.removeListener("UserSelectedRows", this._onUserSelectedRows);
    }

    _showDefaultMessageDisplay() {
        return this._gameEditor
            .showDefaultMessageView({
                message: "No future occurrence available"
            })
            .then(() => {
                this._defaultViewActive = true;
            })
            .catch((err) => {
                this._logger.warn({ error: err }, "WorkTrigFutureViewRenderer._showDefaultMessageDisplay error");
            });
    }

    _hideDefaultMessageDisplay() {
        return this._gameEditor
            .hideDefaultMessageView()
            .then(() => {
                this._defaultViewActive = false;
            })
            .catch((err) => {
                this._logger.warn({ error: err }, "WorkTrigFutureViewRenderer._hideDefaultMessageDisplay error");
            });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();

        let shutdowns = [this._futureGrid.shutdown()];
        return Promise.all(shutdowns).then(
            () => {
                this._model = undefined;
                this._gameEditor = undefined;
                this._futureGrid = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            },
            (err) => {
                this.model._logger.warn(err, "WorkTrigFutureViewRenderer.shutdown() error");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return this._model.whenModelIsReady().then(() => {
            this._gameEditor.renderer.rootElement.classList.add("worktrig-future-grid-view-gl");

            this._futureGrid = new EntityGrid({
                gridKey: this._model._gridKey,
                logger: this._logger,
                selectable: false,
                columns: this._model.columnConfig,
                pagingFeed: this._model.pagingFeed,
                showHeader: true
            });

            if (!this._rootElem.hasChildNodes()) {
                this._rootElem.appendChild(this._futureGrid.renderer.rootElement);
            }
            this._onFeedRefreshView(); //on load, show default message as if a refresh was done
            this._registerListeners();
        });
    }

    get rootElement() {
        return this._rootElem;
    }

    get model() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onUserSorted(sortData) {
        this._model.setSort(sortData);
    }

    _onFeedRefreshView() {
        let totalEntities = this._model.pagingFeed.totalEntities;
        if (totalEntities === 0) {
            if (!this._defaultViewActive) {
                this._showDefaultMessageDisplay();
            }
        } else {
            if (this._defaultViewActive) {
                this._hideDefaultMessageDisplay();
            }
        }
    }

    _onUserSelectedRows(selectedRowIds) {
        this._model.updateExplorerState("selectedIds", selectedRowIds || []);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /*******************************************/
}

module.exports = WorkTrigFutureViewRenderer;
